import type { ColumnDef } from "@tanstack/react-table";
import { Download, Loader2, RotateCcw } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { backupDownloadUrl, type Backup } from "@/lib/backups.api";
import {
  formatBytes,
  formatDateTime,
  humanizeTriggeredBy,
  STATUS_LABELS,
  statusTone,
  TIER_LABELS,
} from "./backups.helpers";

function StatusBadge({ status }: { status: Backup["status"] }) {
  const tone = statusTone(status);
  return (
    <span
      className={cn(
        "inline-block rounded px-2 py-0.5 text-[10.5px] font-semibold",
        tone === "danger" && "bg-danger-bg text-danger-text",
        tone === "warning" && "bg-warning-bg text-warning-text",
        tone === "neutral" && "bg-surface-muted text-body",
      )}
    >
      {STATUS_LABELS[status]}
    </span>
  );
}

interface CreateBackupColumnsOptions {
  onRetry: (id: number) => void;
  retryingId: number | null;
}

export function createBackupColumns({
  onRetry,
  retryingId,
}: CreateBackupColumnsOptions): ColumnDef<Backup, any>[] {
  return [
    {
      accessorKey: "startedAt",
      header: "Date / heure",
      cell: ({ row }) => (
        <span className="text-[12px] text-body">{formatDateTime(row.original.startedAt)}</span>
      ),
    },
    {
      accessorKey: "tier",
      header: "Palier",
      cell: ({ row }) => (
        <span className="text-[12px] text-body">{TIER_LABELS[row.original.tier]}</span>
      ),
    },
    {
      accessorKey: "status",
      header: "Statut",
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      accessorKey: "fileSizeBytes",
      header: "Taille",
      cell: ({ row }) => {
        const { fileSizeBytes, filePath, prunedAt } = row.original;
        if (prunedAt) return <span className="text-[12px] text-muted-foreground">Purgée</span>;
        if (!fileSizeBytes || !filePath) return <span className="text-[12px] text-muted-foreground">—</span>;
        return <span className="text-[12px] text-body">{formatBytes(Number(fileSizeBytes))}</span>;
      },
    },
    {
      accessorKey: "triggeredBy",
      header: "Déclenché par",
      cell: ({ row }) => (
        <span className="text-[12px] text-muted-foreground">
          {humanizeTriggeredBy(row.original.triggeredBy)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const backup = row.original;
        const isRetrying = retryingId === backup.id;

        if (backup.status === "failed") {
          return (
            <Button size="sm" variant="secondary" disabled={isRetrying} onClick={() => onRetry(backup.id)}>
              {isRetrying ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
              Relancer
            </Button>
          );
        }

        if (backup.status === "success" && backup.filePath && !backup.prunedAt) {
          return (
            <a
              href={backupDownloadUrl(backup.id)}
              className="inline-flex items-center gap-1.5 text-[12px] font-medium text-brand-gold-dark hover:underline"
            >
              <Download size={12} />
              Télécharger
            </a>
          );
        }

        return null;
      },
    },
  ];
}
